import { ReactElement, MouseEvent, Children } from 'react';

import MUIButtonGroup from '@mui/material/ButtonGroup';
import classNames from 'classnames';

import { ButtonSize } from '../Button';

import { useStyles } from './ButtonGroup.styles';

/**
 * Свойства компонента {@link ButtonGroup}
 */
export interface ButtonGroupProps {
  /**
   * Имя класса для группы кнопок.
   */
  className?: string;

  /**
   * Идентификатор для группы кнопок.
   */
  id?: string;

  /**
   * Кнопочные элементы, которые будут отображаться внутри группы.
   */
  children: ReactElement[] | ReactElement;

  /**
   * Ориентация группы кнопок.
   *
   * @defaultValue `'horizontal'`
   */
  orientation?: 'horizontal' | 'vertical';

  /**
   * Вариант группы кнопок.
   *
   * @defaultValue `'contained'`
   */
  variant?: 'contained' | 'silver';

  /**
   * Определяет, должны ли кнопки в группе быть разделены. Действует, только когда variant="contained" и orientation="vertical".
   *
   * @defaultValue `false`
   */
  separated?: boolean;

  /**
   * Размер кнопок в группе.
   *
   * @defaultValue `'medium'`
   */
  size?: ButtonSize;

  /**
   * Определяет, должна ли группа кнопок быть отключена.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Обработчик события клика.
   */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
}

/**
 * Пользовательский компонент группы кнопок, построенный с использованием Material-UI.
 *
 * @public
 * @param className - Имя класса для группы кнопок.
 * @param id - Идентификатор для группы кнопок.
 * @param children - Кнопочные элементы, которые будут отображаться внутри группы.
 * @param orientation - Ориентация группы кнопок.
 * @param variant - Вариант группы кнопок.
 * @param separated - Определяет, должны ли кнопки в группе быть разделены. Действует, только когда variant="contained" и orientation="vertical".
 * @param size - Размер кнопок в группе.
 * @param disabled - Определяет, должна ли группа кнопок быть отключена.
 * @param onClick - Обработчик события клика.
 * @returns Компонент ButtonGroup.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ButtonGroupProps}
 */
export const ButtonGroup = ({
  className,
  children,
  orientation = 'horizontal',
  variant = 'contained',
  separated = false,
  size = 'medium',
  disabled = false,
  ...props
}: ButtonGroupProps) => {
  const { classes } = useStyles();

  const newChildren = Children.map(
    children as ReactElement<Record<string, unknown>>,
    (childElement: ReactElement<Record<string, unknown>>) => {
      const { className: classNameElement, ...propsElement } =
        childElement.props;

      const buttonType = childElement.type as { name?: string };
      const isButton = buttonType?.name === 'Button';

      return {
        ...childElement,
        props: {
          ...propsElement,
          size,
          className: classNames(
            classNameElement as string,
            classes.element,
            variant === 'contained' && classes.elementContained,
            variant === 'silver' && classes.elementSilver,
            separated && classes.elementSeparated,
            isButton && variant === 'contained' && classes.buttonContainedHover,
            isButton && variant === 'silver' && classes.buttonSilverHover,
          ),
        },
      };
    },
  );

  const buttonGroupStyles = classNames(
    classes.buttonGroup,
    separated && classes.buttonGroupSeparated,
    variant === 'silver' && classes.buttonGroupSilver,
    variant === 'silver' && disabled && classes.buttonGroupSilverDisabled,
    size === 'large' && classes.sizeLarge,
    className,
    'aqa_button_group',
  );

  return (
    <MUIButtonGroup
      {...props}
      className={buttonGroupStyles}
      children={newChildren}
      disabled={disabled}
      orientation={orientation}
      variant="contained"
    />
  );
};
